import {t, Trans} from '@lingui/macro'
import {Link as RouterLink} from 'react-router-dom'

import type {ButtonVariant} from '@pleo-io/telescope'
import {Link, Stack, Text, tokens, Tooltip} from '@pleo-io/telescope'

import {BillingInterval, Interval, PlanTypeName} from '@product-web/api/types/billing'
import {PlanType} from '@product-web/api/types/plan'
import type {Subscription} from '@product-web/bff-moons/generated/beyond'
import {getFeatureCountryConfiguration} from '@product-web/country-configuration/configuration'
import {Feature} from '@product-web/country-configuration/types'
import {reportError} from '@product-web/error/report'
import type Country from '@product-web/locale/country'
import {useUser} from '@product-web/shell/user'

import {
    BillingText,
    ButtonCenter,
    ChangePlanButtonBox,
    DiscountText,
    HeaderText,
    ListWrapper,
    PlanPill,
    PlanPillBox,
    PlanTitleWrapper,
    PricingText,
    RecommendedPlanIcon,
    StyledBenefitsList,
    YellowBox,
} from './modal-choose-plans.styles'
import type {PlanBenefit} from './plan-benefits-list'
import type {PricePlanPrices} from './pricing-helpers'
import {isPayingAnnually, toStringNoDecimal} from './pricing-helpers'

export interface PlanOptionProps {
    benefits: {[key: string]: PlanBenefit[]}
    buttonOnClick: () => void
    buttonText: string
    header: string
    buttonVariant: ButtonVariant
    planHeader: string
    prices?: PricePlanPrices
    planSubtitleText?: string
    subscription: Subscription | null
    planType?: PlanType
    isEligible?: boolean
    onDismiss: Function
    isOwner: boolean
    startPlanUserLimit: number
    recommended?: boolean
    commitmentInterval?: Interval.MONTH | Interval.YEAR
    billingInterval?: BillingInterval
    planCurrency?: string
    country?: Country
}

const PlanOption = ({
    benefits,
    buttonOnClick,
    buttonText,
    header,
    buttonVariant,
    planHeader,
    prices,
    planSubtitleText,
    subscription,
    planType,
    isEligible,
    onDismiss,
    isOwner,
    startPlanUserLimit,
    recommended,
    commitmentInterval,
    billingInterval,
    planCurrency,
    country,
}: PlanOptionProps) => {
    const {isCashBackCountry} = getFeatureCountryConfiguration(Feature.Paywall, country)

    const showDiscountText = isPayingAnnually(
        commitmentInterval,
        billingInterval,
        isCashBackCountry,
    )

    const isStarter = planType && [PlanType.FREE, PlanType.STARTER].includes(planType)

    const currentPlan = subscription?.platformRatePlan
    const user = useUser()
    const numberOfUsersInCompany = user?.company?.numberOfUsers

    const willShowMoreThanThreeUsersBox = numberOfUsersInCompany
        ? numberOfUsersInCompany > startPlanUserLimit
        : true

    return (
        <ListWrapper>
            <PlanTitleWrapper shouldOverflow={willShowMoreThanThreeUsersBox}>
                <Stack>
                    <Text variant="xlarge-accent" as="h3">
                        {planHeader}
                    </Text>
                    <PriceText
                        plan={planType!}
                        priceAmounts={prices!}
                        currency={planCurrency!}
                        showDiscountText={showDiscountText}
                        isStarter={isStarter}
                    />
                    <BillingSubtitleText
                        isStarter={isStarter}
                        prices={prices}
                        commitmentInterval={commitmentInterval}
                        planCurrency={planCurrency}
                        hasCurrentPlan={!!currentPlan}
                        isEligible={isEligible}
                        startPlanUserLimit={startPlanUserLimit}
                        planType={planType}
                        onDismiss={onDismiss}
                    />
                </Stack>
                {recommended && <RecommendedPlanIcon />}
            </PlanTitleWrapper>

            <ChangePlanButton
                buttonOnClick={buttonOnClick}
                buttonText={buttonText}
                buttonVariant={buttonVariant}
                isOwner={isOwner}
                subscription={subscription}
                commitmentInterval={commitmentInterval}
                isEligible={isEligible}
                isStarter={isStarter}
                planType={planType}
                billingInterval={billingInterval}
            />

            {planSubtitleText && (
                <Text space={20} color="shade800" variant="medium-default">
                    {planSubtitleText}
                </Text>
            )}

            <HeaderText>{header}</HeaderText>

            {Object.keys(benefits).map((category) => {
                return (
                    <StyledBenefitsList
                        key={category}
                        style={{
                            marginBottom: !isEligible ? tokens.spacing48 : tokens.spacing52,
                        }}
                        benefits={benefits[category]}
                        title={category}
                    />
                )
            })}
        </ListWrapper>
    )
}

export default PlanOption

interface BillingSubtitleProps {
    isStarter: boolean | undefined
    prices?: PricePlanPrices
    commitmentInterval: Interval.MONTH | Interval.YEAR | undefined
    planCurrency?: string
    hasCurrentPlan?: boolean
    isEligible?: boolean
    startPlanUserLimit: number
    planType: PlanType | undefined
    onDismiss: Function
}

const BillingSubtitleText = ({
    isStarter,
    prices,
    commitmentInterval,
    planCurrency,
    hasCurrentPlan,
    isEligible,
    startPlanUserLimit,
    planType,
    onDismiss,
}: BillingSubtitleProps) => {
    if (isStarter) {
        return (
            <MoreThanThreeUsersBox
                hasCurrentPlan={hasCurrentPlan}
                isEligible={isEligible}
                startPlanUserLimit={startPlanUserLimit}
                planType={planType}
                onDismiss={onDismiss}
            />
        )
    }

    const formattedPrice = toStringNoDecimal(prices?.yearlyDiscounted, planCurrency!)

    let billingText = t`Billed annually at ${formattedPrice}, renews every 12 months`

    if (commitmentInterval === Interval.MONTH) {
        billingText = t`Billed monthly, renews every month`
    }

    return (
        <BillingText variant="small-subtle" color="shade800" weight="medium">
            {billingText}
        </BillingText>
    )
}

interface MoreThanThreeUsersInterface {
    hasCurrentPlan?: boolean
    isEligible?: boolean
    startPlanUserLimit: number
    planType: PlanType | undefined
    onDismiss: Function
}

const MoreThanThreeUsersBox = ({
    hasCurrentPlan,
    isEligible,
    startPlanUserLimit,
    planType,
    onDismiss,
}: MoreThanThreeUsersInterface) => {
    return hasCurrentPlan && !isEligible ? (
        <YellowBox marginTop={8} marginRight={2}>
            <Text variant="small-subtle" color="shade800" data-testid="user-limit-message">
                <Trans>
                    You have more than {startPlanUserLimit} users. To continue with{' '}
                    {PlanTypeName[planType!]}, remove users at
                </Trans>{' '}
                <Link
                    as={RouterLink}
                    to="/people"
                    onClick={() => onDismiss()}
                    color="inherit"
                    style={{
                        fontSize: tokens.fontSmall,
                    }}
                >
                    {t`People page`}
                </Link>
            </Text>
        </YellowBox>
    ) : (
        <></>
    )
}

interface PriceTextProps {
    plan: PlanType
    priceAmounts: PricePlanPrices
    currency: string
    showDiscountText: boolean
    isStarter: boolean | undefined
}
const PriceText = ({plan, priceAmounts, currency, showDiscountText, isStarter}: PriceTextProps) => {
    if (plan === PlanType.STARTER) {
        return <Text color="shade800" variant="3xlarge-accent">{t`Free`}</Text>
    }

    return (
        <>
            <PricingText color="shade800" variant="3xlarge-accent">
                {showDiscountText ? (
                    <>
                        {toStringNoDecimal(priceAmounts?.monthlyDiscounted, currency)}
                        <DiscountText color="shade600" as="span">
                            {toStringNoDecimal(priceAmounts?.monthly, currency)}
                        </DiscountText>
                    </>
                ) : (
                    toStringNoDecimal(priceAmounts?.monthly, currency)
                )}
                {!isStarter && (
                    <Text color="shade800" variant="small-subtle">
                        {'/'}
                        {t`month`}
                    </Text>
                )}
            </PricingText>
        </>
    )
}

interface ChangePlanButtonProps {
    buttonOnClick: () => void
    buttonText: string
    buttonVariant: ButtonVariant
    isOwner: boolean
    commitmentInterval: Interval.MONTH | Interval.YEAR | undefined
    subscription: Subscription | null
    planType?: PlanType
    isEligible?: boolean
    isStarter?: boolean
    billingInterval?: BillingInterval
}

const ChangePlanButton = ({
    buttonOnClick,
    buttonText,
    buttonVariant,
    isOwner,
    isStarter,
    subscription,
    isEligible,
    commitmentInterval,
    planType,
    billingInterval,
}: ChangePlanButtonProps) => {
    let tooltipText = ''

    const currentPlan = subscription?.platformRatePlan
    const isPremiumOrBusinessOrAdvanced =
        currentPlan &&
        [PlanType.PREMIUM, PlanType.BUSINESS, PlanType.ADVANCED].some((p) => p === currentPlan.type)

    const isCurrentPlanYearlyContract =
        (subscription?.billingPeriodType === Interval.YEAR ||
            subscription?.termType === 'TERMED') &&
        currentPlan?.type !== PlanType.STARTER

    const currentPlanCommitmentInterval = isCurrentPlanYearlyContract
        ? Interval.YEAR
        : Interval.MONTH
    const currentPlanBillingInterval =
        subscription?.billingPeriodType === Interval.YEAR
            ? BillingInterval.YEARLY
            : BillingInterval.MONTHLY

    const isCurrentPlanSelected =
        currentPlan?.type === planType &&
        (planType === PlanType.STARTER ||
            ((!commitmentInterval || commitmentInterval === currentPlanCommitmentInterval) &&
                (!billingInterval || currentPlanBillingInterval === billingInterval)))

    const isDisabled =
        !isEligible ||
        isPremiumOrBusinessOrAdvanced ||
        isCurrentPlanSelected ||
        !currentPlan ||
        !isOwner ||
        isCurrentPlanYearlyContract ||
        (subscription?.billingPeriodType === Interval.YEAR &&
            (commitmentInterval === Interval.MONTH || isStarter))

    const isCurrentUserPlan =
        isPremiumOrBusinessOrAdvanced &&
        planType &&
        ![PlanType.PREMIUM, PlanType.BUSINESS, PlanType.ADVANCED].includes(planType)

    const isSwitchingFromYearlyToMonthlyPlan =
        subscription?.billingPeriodType === Interval.YEAR && commitmentInterval === Interval.MONTH

    if (!currentPlan) {
        tooltipText = t`Company verification in progress. Please wait until you're fully onboarded before changing plans.`
    } else if (isCurrentPlanYearlyContract) {
        tooltipText = t`To change your plan, please contact your account executive`
    } else if (isCurrentUserPlan) {
        const planName =
            PlanTypeName[
                getIsSupportedPlanType(currentPlan.type) ? currentPlan.type : PlanType.UNKNOWN
            ]
        tooltipText = t`To downgrade from ${planName}, please contact your account executive.`
    } else if (isSwitchingFromYearlyToMonthlyPlan) {
        tooltipText = t`To downgrade from a yearly plan, please contact your account executive.`
    }

    if (isCurrentPlanSelected) {
        return (
            <ChangePlanButtonBox>
                <PlanPillBox>
                    <PlanPill data-testid={`current-plan-text`}>
                        <Text variant="xsmall-subtle" color="shade800">
                            <Trans>Your current plan</Trans>
                        </Text>
                    </PlanPill>
                </PlanPillBox>
            </ChangePlanButtonBox>
        )
    }
    return (
        <Tooltip content={tooltipText}>
            <ChangePlanButtonBox>
                <ButtonCenter
                    css={{pointerEvents: isDisabled ? 'none' : 'auto'}}
                    variant={buttonVariant}
                    onClick={buttonOnClick}
                    disabled={isDisabled}
                    data-testid={`choose-plan-button-${planType}`}
                >
                    {buttonText}
                </ButtonCenter>
            </ChangePlanButtonBox>
        </Tooltip>
    )
}

interface LegacyPriceProps {
    priceText: string
}
export const LegacyPriceText = ({priceText}: LegacyPriceProps) => {
    return (
        <Text css={{marginTop: '20px'}} color="shade800" variant="large-accent">
            {priceText}
        </Text>
    )
}

const getIsSupportedPlanType = (planType: string): planType is PlanType => {
    const isSupportedPlanType = !!Object.values(PlanType).some((p) => p === planType)

    if (!isSupportedPlanType) {
        reportError(`Unsupported plan type`, undefined, {planType})
    }

    return isSupportedPlanType
}
